import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
  Button,
} from 'reactstrap';
import * as moment from 'moment';
import { Link } from 'react-router-dom';
import BreadCrumb from '../Components/Common/BreadCrumb';
import TableContainer from '../Components/Common/TableContainer';
import DeleteModal from '../Components/Common/DeleteModal';
import { TariffContext } from '../context/TariffContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Loader from '../Components/Common/Loader';
import ExportCSVModal from '../Components/Common/ExportCSVModal';

const TariffManagement = () => {
  document.title = 'مدیریت تعرفه‌ها | فات 90';

  const { tariffs, loading, error, fetchTariffs, addTariff, editTariff, removeTariff } = useContext(TariffContext);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [tariff, setTariff] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);
  const [isExportCSV, setIsExportCSV] = useState(false);

  // اعتبارسنجی فرم
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (tariff && tariff.name) || '',
      type: (tariff && tariff.type) || '',
      amount: (tariff && tariff.amount) || '',
      price: (tariff && tariff.price) || '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('لطفاً نام تعرفه را وارد کنید')
        .max(100, 'نام تعرفه باید حداکثر 100 کاراکتر باشد'),
      type: Yup.string()
        .required('لطفاً نوع تعرفه را وارد کنید')
        .max(50, 'نوع تعرفه باید حداکثر 50 کاراکتر باشد'),
      amount: Yup.number()
        .required('لطفاً مقدار را وارد کنید')
        .min(0, 'مقدار نمی‌تواند منفی باشد'), 
      price: Yup.number()
        .required('لطفاً قیمت را وارد کنید')
        .min(0, 'قیمت نمی‌تواند منفی باشد'),

    }),
    onSubmit: async (values) => {
      try {
        const tariffDto = {
          name: values.name,
          type: values.type,
          amount: parseFloat(values.amount),
          price: parseFloat(values.price),
        };
        if (isEdit) {
          await editTariff(tariff.id, tariffDto);
        } else {
          await addTariff(tariffDto);
        }
        toggle();
        validation.resetForm();
      } catch (err) {
        // خطاها توسط TariffContext مدیریت می‌شن
      }
    },
  });

  // دریافت تعرفه‌ها در بارگذاری اولیه
  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  // تغییر مودال
  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setTariff(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  }, [modal, validation]);

  // کلیک برای افزودن تعرفه
  const handleTariffClicks = () => {
    setTariff(null);
    setIsEdit(false);
    toggle();
  };

  // کلیک برای ویرایش تعرفه
  const handleTariffClick = useCallback(
    (tariffData) => {
      setTariff({
        id: tariffData.id,
        name: tariffData.name,
        type: tariffData.type,
        amount: tariffData.amount,
        price: tariffData.price,
      });
      setIsEdit(true);
      toggle();
    },
    [toggle]
  );

  // حذف تکی
  const onClickDelete = (tariff) => {
    setTariff(tariff);
    setDeleteModal(true);
  };

  const handleDeleteTariff = async () => {
    if (tariff) {
      try {
        await removeTariff(tariff.id);
        setDeleteModal(false);
      } catch (err) {
        // خطاها توسط TariffContext مدیریت می‌شن
      }
    }
  };

  // انتخاب چندگانه
  const checkedAll = useCallback(() => {
    const checkall = document.getElementById('checkBoxAll');
    const ele = document.querySelectorAll('.tariffCheckBox');
    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    deleteCheckbox();
  }, []);

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll('.tariffCheckBox:checked');
    ele.length > 0 ? setIsMultiDeleteButton(true) : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  const deleteMultiple = async () => {
    const checkall = document.getElementById('checkBoxAll');
    try {
      for (const element of selectedCheckBoxDelete) {
        await removeTariff(parseInt(element.value));
      }
      setIsMultiDeleteButton(false);
      checkall.checked = false;
      setDeleteModalMulti(false);
    } catch (err) {
      // خطاها توسط TariffContext مدیریت می‌شن
    }
  };

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell) => (
          <input
            type="checkbox"
            className="tariffCheckBox form-check-input"
            value={cell.getValue()}
            onChange={() => deleteCheckbox()}
          />
        ),
        id: '#',
        accessorKey: 'id',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'نام تعرفه',
        accessorKey: 'name',
        enableColumnFilter: false,
      },
      {
        header: 'نوع',
        accessorKey: 'type',
        enableColumnFilter: false,
      },
      {
        header: 'مقدار',
        accessorKey: 'amount',
        enableColumnFilter: false,
        cell: (cell) => cell.getValue().toFixed(2),
      },
      {
        header: 'قیمت',
        accessorKey: 'price',
        enableColumnFilter: false,
        cell: (cell) => cell.getValue().toFixed(2),
      },
      {
        header: 'آخرین به‌روزرسانی',
        accessorKey: 'lastUpdated',
        enableColumnFilter: false,
        cell: (cell) => moment(new Date(cell.getValue())).format('DD MMM YYYY'),
      },
      {
        header: 'عملیات',
        cell: (cellProps) => (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item edit">
              <Link
                to="#"
                className="text-primary d-inline-block edit-item-btn"
                onClick={() => {
                  const tariffData = cellProps.row.original;
                  handleTariffClick(tariffData);
                }}
              >
                <i className="ri-pencil-fill fs-16"></i>
              </Link>
            </li>
            <li className="list-inline-item">
              <Link
                to="#"
                className="text-danger d-inline-block remove-item-btn"
                onClick={() => {
                  const tariffData = cellProps.row.original;
                  onClickDelete(tariffData);
                }}
              >
                <i className="ri-delete-bin-5-fill fs-16"></i>
              </Link>
            </li>
          </ul>
        ),
      },
    ],
    [handleTariffClick, checkedAll]
  );

  return (
    <div className="page-content">
      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={tariffs}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteTariff}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={deleteMultiple}
        onCloseClick={() => setDeleteModalMulti(false)}
      />

      <Container fluid>
        <BreadCrumb title="مدیریت تعرفه‌ها" pageTitle="مدیریت" />
        <Row>
          <Col lg={12}>
            <Card id="tariffList">
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">لیست تعرفه‌ها</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <Button
                        color="primary"
                        className="add-btn"
                        onClick={handleTariffClicks}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> افزودن تعرفه
                      </Button>
                      <Button
                        color="info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i> خروجی CSV
                      </Button>
                      {isMultiDeleteButton && (
                        <Button
                          color="soft-danger"
                          onClick={() => setDeleteModalMulti(true)}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  {loading ? (
                    <Loader error={error} />
                  ) : tariffs.length ? (
                    <TableContainer
                      columns={columns}
                      data={tariffs}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={8}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light text-muted"
                      handleOrderClick={handleTariffClicks}
                      isOrderFilter={false}
                      SearchPlaceholder="جستجو بر اساس نام تعرفه، نوع یا مقدار..."
                    />
                  ) : (
                    <div className="text-center py-4">
                      <h5>هیچ تعرفه‌ای یافت نشد</h5>
                    </div>
                  )}
                </div>

                <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {isEdit ? 'ویرایش تعرفه' : 'افزودن تعرفه'}
                  </ModalHeader>
                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <div className="mb-3">
                        <Label htmlFor="name-field" className="form-label">
                          نام تعرفه
                        </Label>
                        <Input
                          name="name"
                          id="name-field"
                          className="form-control"
                          placeholder="نام تعرفه را وارد کنید"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name}
                          invalid={validation.touched.name && !!validation.errors.name}
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback>{validation.errors.name}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="type-field" className="form-label">
                          نوع تعرفه
                        </Label>
                        <Input
                          name="type"
                          id="type-field"
                          className="form-control"
                          placeholder="نوع تعرفه را وارد کنید"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.type}
                          invalid={validation.touched.type && !!validation.errors.type}
                        />
                        {validation.touched.type && validation.errors.type ? (
                          <FormFeedback>{validation.errors.type}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="amount-field" className="form-label">
                          مقدار
                        </Label>
                        <Input
                          name="amount"
                          id="amount-field"
                          className="form-control"
                          placeholder="مقدار را وارد کنید"
                          type="number"
                          step="0.01"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.amount}
                          invalid={validation.touched.amount && !!validation.errors.amount}
                        />
                        {validation.touched.amount && validation.errors.amount ? (
                          <FormFeedback>{validation.errors.amount}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="price-field" className="form-label">
                          قیمت
                        </Label>
                        <Input
                          name="price"
                          id="price-field"
                          className="form-control"
                          placeholder="قیمت را وارد کنید"
                          type="number"
                          step="0.01"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.price}
                          invalid={validation.touched.price && !!validation.errors.price}
                        />
                        {validation.touched.price && validation.errors.price ? (
                          <FormFeedback>{validation.errors.price}</FormFeedback>
                        ) : null}
                      </div>
                    </ModalBody>
                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <Button
                          color="light"
                          onClick={() => setModal(false)}
                        >
                          بستن
                        </Button>
                        <Button
                          type="submit"
                          color="success"
                          disabled={loading}
                        >
                          {isEdit ? 'به‌روزرسانی' : 'افزودن'}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Modal>
                <ToastContainer closeButton={false} limit={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TariffManagement;